import { PlusCircleFilled, ReloadOutlined } from "@ant-design/icons";
import {
  Card,
  Col,
  message,
  Row,
  Select,
  Button as AntButton,
} from "antd";
import { Form, Input, Button } from "antd";
import axios from "axios";
import Cookies from "js-cookie";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { GetTokenProducto, TypeIva } from "../../api/utils";
import { productStore } from "./ProductSimple";
import { baseurl, baseurlwc, credentials } from "../../api/api";
import { useProductStore } from "./LayoutProducto";
import { ContableItems } from "./ContableItems";
import { useAttributesStore } from "./Atributes";

const { Option } = Select;
const MAX_STICKER_REFERENCE_LENGTH = 16;
const COLOR_ABBREVIATIONS = {
  amarillo: "AM",
  azul: "AZ",
  beige: "BG",
  blanco: "BL",
  cafe: "CF",
  "café": "CF",
  fucsia: "FC",
  gris: "GR",
  lila: "LI",
  marfil: "MRL",
  menta: "MT",
  mostaza: "MS",
  negro: "NG",
  naranja: "NJ",
  palo: "PL",
  pastel: "PT",
  rosado: "RS",
  rosa: "RS",
  rojo: "RJ",
  verde: "VD",
  sabila: "SB",
  "sábila": "SB",
  claro: "CL",
  clara: "CL",
  oscuro: "OS",
  oscura: "OS",
};

const normalizeReferenceSegment = (value = "") =>
  String(value)
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "-")
    .replace(/[^A-Z0-9-]/g, "");

const isColorOrSizeAttribute = (attribute = {}) => {
  const attributeName = String(attribute.name || "").toLowerCase();
  return attributeName.includes("color") || attributeName.includes("talla");
};

const getColorCode = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => COLOR_ABBREVIATIONS[word] || word.slice(0, 2).toUpperCase())
    .join("");

const getSizeCode = (value = "") => {
  const normalizedValue = String(value).trim();
  const compactValue = normalizedValue.toUpperCase().replace(/\s+/g, "");

  if (!compactValue) return "";
  if (compactValue === "UNICA" || compactValue === "TALLAUNICA") return "TU";

  return compactValue;
};

const getVariantAttributeCode = (attribute = {}) => {
  const attributeName = String(attribute.name || "").toLowerCase();
  const option = String(attribute.option || "").trim();

  if (!option) return "";
  if (attributeName.includes("color")) return getColorCode(option);
  if (attributeName.includes("talla")) return getSizeCode(option);

  return normalizeReferenceSegment(option);
};

const buildVariantReferenceComplement = (attributes = [], maxLength = MAX_STICKER_REFERENCE_LENGTH) =>
  attributes
    .filter(isColorOrSizeAttribute)
    .map((attribute) => getVariantAttributeCode(attribute))
    .filter(Boolean)
    .join("")
    .trim()
    .slice(0, Math.max(0, maxLength));

const buildVariantReferenceCode = (baseCode, complement = "") => {
  const normalizedBase = String(baseCode || "").trim().toUpperCase();
  const normalizedComplement = String(complement || "")
    .trim()
    .toUpperCase();

  if (!normalizedBase) {
    return "";
  }

  return `${normalizedBase}${normalizedComplement ? ` ${normalizedComplement}` : ""}`
    .trim()
    .slice(0, MAX_STICKER_REFERENCE_LENGTH);
};

export const ProductoVariante = ({
  addProductSimple,
  variation = false,
  reloadAttributes,
}) => {
  const producto = useProductStore();
  const attributes = useAttributesStore();

  const categorys = Array.isArray(producto.categorys) ? producto.categorys : [];

  // 🔥 MARCAS = ATRIBUTO ID 1
  const brands = Array.isArray(attributes?.groups?.[1])
    ? attributes.groups[1]
    : [];

  const attributesData = attributes?.data || {};
  const attributesList = Array.isArray(attributes?.list) ? attributes.list : [];

  const selectedAttributes = Array.isArray(attributes?.selected)
    ? attributes.selected
    : [];
  const baseReferenceCode = String(producto?.data?.Codigo || "")
    .trim()
    .toUpperCase();
  const maxBaseReferenceLength = MAX_STICKER_REFERENCE_LENGTH;
  const remainingComplementLength = Math.max(
    0,
    MAX_STICKER_REFERENCE_LENGTH - Math.min(baseReferenceCode.length, MAX_STICKER_REFERENCE_LENGTH),
  );

  const [form] = Form.useForm();
  const [termselected, settermselected] = useState({});
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [lastSuggestedCode, setLastSuggestedCode] = useState("");

  // Validar que sea un producto usando useEffect
  useEffect(() => {
    if (!producto.isProduct) {
      message.error("Solo se pueden crear productos, no servicios.");
    }
  }, [producto.isProduct]);

  useEffect(() => {
    if (!variation) return;

    const selectedReferenceAttributes = selectedAttributes
      .filter((id) => Number(id) !== 1)
      .map((id) => {
        const attribute = attributesList.find(
          (item) => String(item.id) === String(id),
        );

        return {
          id,
          name: attribute?.name,
          option: termselected[id] || "",
        };
      });

    const suggestedComplement = buildVariantReferenceComplement(
      selectedReferenceAttributes,
      remainingComplementLength,
    );
    const currentComplement = form.getFieldValue("CodigoComplemento");

    if (!currentComplement || currentComplement === lastSuggestedCode) {
      form.setFieldsValue({ CodigoComplemento: suggestedComplement });
      setLastSuggestedCode(suggestedComplement);
    }
  }, [
    variation,
    selectedAttributes,
    attributesList,
    termselected,
    producto.data.Codigo,
    remainingComplementLength,
    form,
    lastSuggestedCode,
  ]);

  // Si no es un producto, no renderizamos el formulario
  if (!producto.isProduct) {
    return null;
  }

  const handleReloadBrands = async () => {
    setLoadingBrands(true);
    try {
      await reloadAttributes();
      toast.success("Marcas actualizadas correctamente");
    } catch (error) {
      toast.error("Error al actualizar marcas");
    } finally {
      setLoadingBrands(false);
    }
  };

  const handleReloadTerms = async () => {
    setLoadingTerms(true);
    try {
      await reloadAttributes();
      toast.success("Términos actualizados correctamente");
    } catch (error) {
      toast.error("Error al actualizar términos");
    } finally {
      setLoadingTerms(false);
    }
  };

  const onFinish = (values) => {
    producto.setData({ productstore: productStore });
    if (variation) {
      addVariante(values);
    } else {
      addProductSimple(productStore);
    }

    form.setFieldsValue({
      ...(variation ? { CodigoComplemento: "" } : { Codigo: "" }),
    });
  };

  toast.configure();

  const onFinishFailed = (errorInfo) => {
    message.info("Por favor complete todos los campos");
  };

  const addVariante = (values) => {
    // 1️⃣ ATRIBUTOS SELECCIONADOS
    const filteredAttributes = selectedAttributes
      .filter((id) => Number(id) !== 1) // ⛔ Marca fuera
      .map((id) => {
        const name = values[`termselected-${id}`];
        const group = attributes.list.find((i) => i.id === id);
        const att = attributes.groups[id]?.find((i) => i.name === name);

        return {
          id: group?.id,
          option: name,
          name: group?.name,
          slug: att?.slug || name.toLowerCase().replace(/\s+/g, "-"),
        };
      });

    // solo COLOR y TALLA (marca no)
    const wooAttributes = filteredAttributes
      .filter((a) => a.id !== 1)
      .map((attr) => ({
        id: attr.id,
        option: attr.option,
      }));

    // 2️⃣ TALLA (size) - Buscar por slug del término seleccionado
    const sizeAttr = filteredAttributes.find(
      (attr) => Number(attr.id) === 3, // ID del atributo talla
    );

    // Usar el slug del término seleccionado para la talla
      const tallaFinal = sizeAttr ? sizeAttr.slug : "00";
      const generatedReferenceCode = buildVariantReferenceCode(
        values.Codigo || producto.data.Codigo,
        values.CodigoComplemento ||
          buildVariantReferenceComplement(filteredAttributes),
      );

    if (!generatedReferenceCode) {
      toast.error(
        "Debes definir primero el código base de referencia del producto variable.",
      );
      return;
    }

    // 3️⃣ Construir objeto para ARI
    let productS = {
      ...producto.data,
      Codigo: generatedReferenceCode,
      IdCategoria: producto.data.IdCategoria,
      IdSubCategoria: producto.data.IdSubCategoria,
      Categoria: producto.data.Categoria,
      SubCategoria: producto.data.SubCategoria,
      PrecioVentaConIva1: values.PrecioVentaConIva1,
      PrecioVentaConIva2: values.PrecioVentaConIva2,
      PrecioVentaConIva3: values.PrecioVentaConIva3,
      PorcentajeIva: values.PorcentajeIva,
      weight: values.weight,
      TipoProducto: 1,
      ParametrizacionContableProducto: [
        { NumeroCuenta: values.cat0 || "", Tipo: 0 },
        { NumeroCuenta: values.cat1 || "", Tipo: 1 },
        { NumeroCuenta: values.cat2 || "", Tipo: 2 },
        { NumeroCuenta: values.cat3 || "", Tipo: 4 },
        { NumeroCuenta: values.cat4 || "", Tipo: 5 },
      ],
    };

    productS.Descripcion =
      Cookies.get("productname") +
      "-" +
      filteredAttributes.map((a) => a.option).join("-");

    const getToken = GetTokenProducto(productS);

    // 4️⃣ Obtener token ARI
    toast.promise(getToken, {
      pending: "Obteniendo token...",
      success: "Token obtenido 👌",
      error: "Error obteniendo token 🤯",
    });

    getToken.then((token) => {
      let { data } = token;
      if (data) {
        let productotosave = {
          ...productS,
          Token: data,
          Talla: [
            {
              Talla: tallaFinal, // Aquí se envía el slug
              Existencia: 0,
              PrecioVenta: productS.PrecioVentaConIva1,
              Token: data,
            },
          ],
        };

        // 5️⃣ Guardar variante en ARI
        axios
          .post(
            baseurl + "GuardarProductoTallasConContabilidad",
            productotosave,
          )
          .then((res) => {
            if (!res.data.Exito) {
              toast.error(res.data.MensajeError);
              return;
            }

            const idproduct = Cookies.get("productid");

            let producttowc = {
              regular_price: `${productS.PrecioVentaConIva1}`,
              manage_stock: true,
              stock_quantity: 0,
              sku: res.data.ListadoCodigoBarrasTalla[0].CodigoBarrasTalla,
              weight: productS.weight,
              dimensions: productS.dimensions,
              attributes: wooAttributes,
            };

            axios
              .post(
                `${baseurlwc}/products/${idproduct}/variations?${credentials}`,
                producttowc,
              )
              .then((resp) => {
                if (resp.data.id) {
                  toast.success("Variación creada con éxito");
                } else {
                  toast.error("No se pudo crear la variación");
                }
              })
              .catch((err) => {
                toast.error(
                  "Error Página Web: " +
                    (err.response?.data?.message || err.message),
                );
              });
          });
      }
    });
  };

  return (
    <Row justify="center" className="product-form-layout" style={{ marginTop: "5px" }}>
        <Col span={24} className="site-card-border-less-wrapper product-form-column">
          <Card
            className="product-form-card"
            hoverable
            bordered
            title={
              variation
                ? "Crear Variante (Puedes crear una o más variantes en este formulario, escogiendo los términos diferentes para cada producto)"
                : "Detalles del producto"
            }
            extra={
              !variation && (
                <AntButton
                  icon={<ReloadOutlined />}
                  onClick={handleReloadBrands}
                  loading={loadingBrands}
                  size="small"
                >
                  Actualizar Marcas
                </AntButton>
              )
            }
            style={{
              borderRadius: "12px",
              margin: 5,
            }}
          >
            <Form
              className="product-form"
              form={form}
              name="basic"
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
                autoComplete="off"
                initialValues={{
                  Codigo: producto.data.Codigo || "",
                  CodigoComplemento: "",
                IdCategoria: producto.data.IdCategoria,
                IdSubCategoria: producto.data.IdSubCategoria,
                webCategoryIds: producto.data.webCategoryIds,
                Descripcion: producto.data.Descripcion,
                PrecioVentaConIva1: producto.data.PrecioVentaConIva1,
                PrecioVentaConIva2: producto.data.PrecioVentaConIva2,
                PrecioVentaConIva3: producto.data.PrecioVentaConIva3,
                PorcentajeIva: producto.data.PorcentajeIva,
                cat0: producto.data.ParametrizacionContableProducto?.find(
                  (item) => item.Tipo === 0,
                )?.NumeroCuenta,
                cat1: producto.data.ParametrizacionContableProducto?.find(
                  (item) => item.Tipo === 1,
                )?.NumeroCuenta,
                cat2: producto.data.ParametrizacionContableProducto?.find(
                  (item) => item.Tipo === 2,
                )?.NumeroCuenta,
                cat3: producto.data.ParametrizacionContableProducto?.find(
                  (item) => item.Tipo === 4,
                )?.NumeroCuenta,
                cat4: producto.data.ParametrizacionContableProducto?.find(
                  (item) => item.Tipo === 5,
                )?.NumeroCuenta,
                weight: producto.data.weight,
                height: producto.data.height,
                width: producto.data.width,
                large: producto.data.large,
              }}
            >
              {!variation && (
              <div style={{ marginBottom: "32px" }}>
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    marginBottom: "16px",
                    color: "#555",
                  }}
                >
                  Información Básica
                </h3>
                <Row
                  gutter={[24, 16]}
                  style={{
                    marginLeft: "-12px",
                    marginRight: "-12px",
                    rowGap: "0px",
                  }}
                >
                  {!variation && (
                    <Col xs={24} lg={24}>
                      <Form.Item
                        label="Nombre del Producto"
                        name="Descripcion"
                        rules={[
                          {
                            required: true,
                            message: "Digite el nombre del producto",
                          },
                        ]}
                        tooltip="Nombre descriptivo del producto"
                      >
                        <Input
                          disabled={variation}
                          placeholder="Ej: Camiseta de Algodón"
                          onChange={(value) =>
                            producto.setData({
                              Descripcion: value.target.value,
                            })
                          }
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {!variation && (
                    <Col xs={24} lg={8}>
                      <Form.Item
                        name="IdCategoria"
                        label="Categoría ARI"
                        rules={[
                          {
                            required: true,
                            message: "Selecciona una Categoría ARI",
                          },
                        ]}
                        tooltip="Selecciona la Categoría ARI principal del producto"
                      >
                        <Select
                          onChange={(value) =>
                            producto.setData({
                              IdCategoria: value,
                              Categoria: producto.knowName(
                                value,
                                producto.categorys,
                              ),
                            })
                          }
                          placeholder="Selecciona una Categoría ARI"
                          allowClear
                          showSearch
                          style={{ width: "100%" }}
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          filterSort={(optionA, optionB) =>
                            optionA.children
                              .toLowerCase()
                              .localeCompare(optionB.children.toLowerCase())
                          }
                        >
                          {categorys.map((category) => (
                            <Option key={category.id} value={category.id}>
                              {category.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  )}
                  {!variation && (
                    <Col xs={24} lg={8}>
                      <Form.Item
                        name="IdSubCategoria"
                        label="SubCategoría ARI"
                        rules={[
                          {
                            required: true,
                            message: "Selecciona una subCategoría ARI",
                          },
                        ]}
                        tooltip="Selecciona una subCategoría ARI"
                      >
                        <Select
                          onChange={(value) =>
                            producto.setData({
                              IdSubCategoria: value,
                              SubCategoria: producto.knowName(
                                value,
                                producto.categorys,
                              ),
                            })
                          }
                          placeholder="Selecciona una subCategoría ARI"
                          allowClear
                          showSearch
                          style={{ width: "100%" }}
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          filterSort={(optionA, optionB) =>
                            optionA.children
                              .toLowerCase()
                              .localeCompare(optionB.children.toLowerCase())
                          }
                        >
                          {producto.categorys.map((category) => (
                            <Option key={category.id} value={category.id}>
                              {category.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  )}
                  {!variation && (
                    <Col xs={24} lg={8}>
                      <Form.Item
                        label={
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <span>Marca</span>
                            <AntButton
                              icon={<ReloadOutlined />}
                              onClick={handleReloadBrands}
                              loading={loadingBrands}
                              size="small"
                              type="link"
                              style={{ marginLeft: "8px" }}
                            >
                              Actualizar
                            </AntButton>
                          </div>
                        }
                        name="IdMarca"
                        tooltip="Selecciona una marca (opcional)"
                      >
                        <Select
                          onChange={(value) => {
                            producto.setData({
                              IdMarca: 1,
                              Marca: value,
                              tags: value ? [value] : [],
                            });
                          }}
                          placeholder="Selecciona una marca"
                          allowClear
                          showSearch
                          notFoundContent={
                            brands.length === 0
                              ? "No hay marcas disponibles. Haz clic en 'Actualizar'"
                              : "No se encontraron marcas"
                          }
                        >
                          {brands.map((brand, index) => (
                            <Option
                              key={brand.id || index}
                              value={brand.name || brand}
                            >
                              {brand.name || brand}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  )}
                  {!variation && (
                    <Col xs={24} lg={24}>
                      <Form.Item
                        name="webCategoryIds"
                        label="Categorías adicionales Página Web"
                        tooltip="Estas categorías se enviarán solo al producto padre de la página web y no cambian la categoría principal en ARI."
                        extra="Puedes seleccionar una o varias categorías adicionales para mostrar este producto variable en más secciones de la web."
                      >
                        <Select
                          mode="multiple"
                          placeholder="Selecciona categorías adicionales para la web"
                          allowClear
                          showSearch
                          style={{ width: "100%" }}
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          filterSort={(optionA, optionB) =>
                            optionA.children
                              .toLowerCase()
                              .localeCompare(optionB.children.toLowerCase())
                          }
                          onChange={(value) =>
                            producto.setData({
                              webCategoryIds: value || [],
                            })
                          }
                        >
                          {producto.categorys.map((category) => (
                            <Option key={category.id} value={category.id}>
                              {category.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </div>
              )}
              {variation && (
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                      marginBottom: "16px",
                      color: "#555",
                    }}
                  >
                    Impuestos
                  </h3>
                  <Row
                    gutter={[24, 16]}
                    style={{
                      marginLeft: "-12px",
                      marginRight: "-12px",
                      rowGap: "0px",
                    }}
                    justify="start"
                  >
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name="PorcentajeIva"
                        label="Porcentaje IVA"
                        rules={[
                          {
                            required: true,
                            message: "Selecciona un porcentaje IVA",
                          },
                        ]}
                        tooltip="Selecciona el porcentaje de IVA aplicable"
                      >
                        <Select
                          onChange={(value) =>
                            producto.setData({ PorcentajeIva: value })
                          }
                          placeholder="Selecciona un porcentaje IVA"
                          allowClear
                          style={{ width: "100%" }}
                        >
                          {TypeIva.map((typeiva) => (
                            <Option key={typeiva.Codigo} value={typeiva.Valor}>
                              {typeiva.Descripcion}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              )}
              {variation && (
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                      marginBottom: "16px",
                      color: "#555",
                    }}
                  >
                    Detalles Contables
                  </h3>
                  <div
                    style={{
                      background: "#FFFADA",
                      padding: "10px",
                      borderRadius: "10px",
                      fontSize: "12px",
                    }}
                  >
                    <p>
                      <strong>Códigos contables por Categoría ARI:</strong>
                    </p>
                    <ul>
                      <li>
                        <strong>ACCESORIOS Y TETEROS</strong> → 14351501,
                        41351501, 61351501, 41750501, 62259501
                      </li>
                      <li>
                        <strong>ASEO Y PAÑALES</strong> → 14351401, 41351401,
                        61351401, 41750501, 61351401
                      </li>
                      <li>
                        <strong>COCHES, CAMINADORES Y BASENILLAS</strong> →
                        14351601, 41351601, 61351601, 41750501, 62259501
                      </li>
                      <li>
                        <strong>TEXTIL</strong> → 14351301, 41351301, 61351301,
                        41750501, 62259501
                      </li>
                    </ul>
                  </div>
                  <ContableItems />
                </div>
              )}

              {variation && (
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                      marginBottom: "16px",
                      color: "#555",
                    }}
                  >
                    Precios y Peso
                  </h3>
                  <Row
                    gutter={[24, 16]}
                    style={{
                      marginLeft: "-12px",
                      marginRight: "-12px",
                      rowGap: "0px",
                    }}
                  >
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Precio de venta Mostrador"
                        name="PrecioVentaConIva1"
                        rules={[
                          {
                            required: true,
                            message: "Digite el precio de venta mostrador",
                          },
                        ]}
                        tooltip="Precio de venta mostrador (incluye IVA)"
                      >
                        <Input
                          placeholder="$200.000"
                          type="number"
                          onChange={(value) =>
                            producto.setData({
                              PrecioVentaConIva1: value.target.value,
                            })
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Precio de venta Minorista"
                        name="PrecioVentaConIva2"
                        rules={[
                          {
                            required: true,
                            message: "Digite el precio de venta minorista",
                          },
                        ]}
                        tooltip="Precio de venta minorista (incluye IVA)"
                      >
                        <Input
                          placeholder="$160.000"
                          type="number"
                          onChange={(value) =>
                            producto.setData({
                              PrecioVentaConIva2: value.target.value,
                            })
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Precio de venta"
                        name="PrecioVentaConIva3"
                        rules={[
                          {
                            required: true,
                            message: "Digite el precio de venta Mayorista",
                          },
                        ]}
                        tooltip="Precio de venta Mayorista (incluye IVA)"
                      >
                        <Input
                          placeholder="$120.000"
                          type="number"
                          onChange={(value) =>
                            producto.setData({
                              PrecioVentaConIva3: value.target.value,
                            })
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        label="Peso del producto"
                        name="weight"
                        rules={[
                          {
                            required: true,
                            message: "Digite el peso del producto",
                          },
                        ]}
                        tooltip="Peso del producto (ej: 1 kg, 0.3 kg)"
                      >
                        <Input
                          placeholder="(1): kg | (0.3): otros"
                          type="text"
                          onChange={(value) =>
                            producto.setData({ weight: value.target.value })
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Largo (cm)"
                        name="length"
                        rules={[{ required: true, message: "Digite el largo" }]}
                        tooltip="Largo del producto en centímetros"
                      >
                        <Input
                          placeholder="Ej: 10"
                          type="number"
                          onChange={(e) =>
                            producto.setData({
                              dimensions: {
                                ...producto.data.dimensions,
                                length: e.target.value,
                              },
                            })
                          }
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Ancho (cm)"
                        name="width"
                        rules={[{ required: true, message: "Digite el ancho" }]}
                        tooltip="Ancho del producto en centímetros"
                      >
                        <Input
                          placeholder="Ej: 5"
                          type="number"
                          onChange={(e) =>
                            producto.setData({
                              dimensions: {
                                ...producto.data.dimensions,
                                width: e.target.value,
                              },
                            })
                          }
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Form.Item
                        label="Alto (cm)"
                        name="height"
                        rules={[{ required: true, message: "Digite el alto" }]}
                        tooltip="Alto del producto en centímetros"
                      >
                        <Input
                          placeholder="Ej: 2"
                          type="number"
                          onChange={(e) =>
                            producto.setData({
                              dimensions: {
                                ...producto.data.dimensions,
                                height: e.target.value,
                              },
                            })
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              )}
              {variation && (
                <div style={{ marginBottom: "32px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                      flexWrap: "wrap",
                      marginBottom: "16px",
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          fontSize: "16px",
                          fontWeight: 500,
                          marginBottom: "4px",
                          color: "#555",
                        }}
                      >
                        Variaciones del Producto
                      </h3>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "13px",
                          color: "#777",
                          lineHeight: 1.5,
                        }}
                      >
                        Solo se muestran los atributos y términos definidos en el
                        paso de atributos.
                      </p>
                    </div>
                    <AntButton
                      icon={<ReloadOutlined />}
                      onClick={handleReloadTerms}
                      loading={loadingTerms}
                      size="small"
                    >
                      Actualizar términos
                    </AntButton>
                  </div>
                  <Row
                    gutter={[24, 16]}
                    style={{
                      marginLeft: "-12px",
                      marginRight: "-12px",
                      rowGap: "0px",
                    }}
                  >
                    {selectedAttributes
                      .filter((id) => Number(id) !== 1)
                      .map((id) => {
                        const attribute = attributesList.find(
                          (item) => String(item.id) === String(id),
                        );

                        if (!attribute) return null;

                        const selectedOptionIds = Array.isArray(attributes.options?.[id])
                          ? attributes.options[id]
                          : [];
                        const selectedOptionNames = Array.isArray(attributesData?.[id])
                          ? attributesData[id].map((value) => String(value).trim())
                          : [];

                        const terms = Array.isArray(attributes.groups?.[id])
                          ? attributes.groups[id].filter((term) =>
                              selectedOptionNames.length > 0
                                ? selectedOptionNames.includes(String(term.name).trim())
                                : selectedOptionIds.length > 0
                                  ? selectedOptionIds.includes(term.id)
                                  : false,
                            )
                          : [];

                        return (
                          <Col xs={24} lg={12} key={id}>
                            <Form.Item
                              name={`termselected-${id}`}
                              label={attribute.name}
                              rules={[
                                {
                                  required: true,
                                  message: `Selecciona un término para ${attribute.name}`,
                                },
                              ]}
                              tooltip={`Selecciona el término para ${attribute.name}`}
                            >
                              <Select
                                placeholder="Selecciona un término"
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                onChange={(value) =>
                                  settermselected((prev) => ({
                                    ...prev,
                                    [id]: value || "",
                                  }))
                                }
                                onClear={() =>
                                  settermselected((prev) => ({
                                    ...prev,
                                    [id]: "",
                                  }))
                                }
                              >
                                {terms.map((termi) => (
                                  <Option
                                    key={termi.id || termi.slug || termi.name}
                                    value={termi.name}
                                  >
                                    {termi.name}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                            {terms.length === 0 && (
                              <div
                                style={{
                                  marginTop: "-8px",
                                  fontSize: "12px",
                                  color: "#8c8c8c",
                                }}
                              >
                                No hay términos seleccionados para{" "}
                                {attribute.name.toLowerCase()} en el paso de
                                atributos.
                              </div>
                            )}
                          </Col>
                        );
                      })}
                  </Row>
                </div>
              )}
              {variation && (
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                      marginBottom: "16px",
                      color: "#555",
                    }}
                  >
                    Código Sticker
                  </h3>
                  <p
                    style={{
                      marginBottom: "16px",
                      color: "#666",
                      fontSize: "14px",
                      lineHeight: 1.5,
                    }}
                  >
                    Este dato es el que quedará visible en los títulos que salen
                    impresos en el sticker.
                  </p>
                  <Row
                    gutter={[24, 16]}
                    style={{
                      marginLeft: "-12px",
                      marginRight: "-12px",
                      rowGap: "0px",
                    }}
                  >
                    <Col xs={24} lg={24}>
                        {(() => {
                        const selectedReferenceAttributes = selectedAttributes
                          .filter((id) => Number(id) !== 1)
                          .map((id) => {
                            const attribute = attributesList.find(
                              (item) => String(item.id) === String(id),
                            );
                            const option = termselected[id];
                            const term = attributes.groups[id]?.find(
                              (item) => item.name === option,
                            );

                            return {
                              id,
                              name: attribute?.name,
                              option,
                              slug: term?.slug || option,
                            };
                          });

                        const suggestedComplement =
                          buildVariantReferenceComplement(
                            selectedReferenceAttributes,
                          );
                          return (
                            <Row gutter={[12, 12]}>
                              <Col xs={24} md={12}>
                                <Form.Item
                                label={
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: "#cf1322",
                                    }}
                                  >
                                    Código Base
                                  </span>
                                }
                                  name="Codigo"
                                  rules={[
                                    {
                                      required: true,
                                      message: "El código base es obligatorio.",
                                    },
                                    {
                                      max: maxBaseReferenceLength,
                                      message: `La base no puede superar ${maxBaseReferenceLength} caracteres.`,
                                    },
                                  ]}
                                  tooltip="Esta base se aplicará a todas las variaciones. No se borra al crear una nueva variación."
                                  extra={
                                    <span className="sticker-code-helper">
                                      La suma entre base y complemento debe dar máximo{" "}
                                      {MAX_STICKER_REFERENCE_LENGTH} caracteres.{" "}
                                      <strong className="sticker-code-helper-highlight">
                                        Con la base actual, quedan{" "}
                                        {remainingComplementLength} caracteres disponibles
                                        para el complemento.
                                      </strong>
                                    </span>
                                  }
                                  required
                                >
                                  <Input
                                    placeholder="Base fija del código de referencia"
                                    maxLength={maxBaseReferenceLength}
                                    showCount
                                    onChange={(e) =>
                                      producto.setData({ Codigo: e.target.value })
                                    }
                                  />
                                </Form.Item>
                              </Col>
                            <Col xs={24} md={12}>
                              <Form.Item
                                label={
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: "#cf1322",
                                    }}
                                  >
                                    Complemento por Color y/o Talla
                                  </span>
                                }
                                name="CodigoComplemento"
                                rules={[
                                  {
                                    required: true,
                                    message:
                                      "El complemento del código es obligatorio.",
                                  },
                                  {
                                    validator: (_, value) => {
                                      const finalCode = buildVariantReferenceCode(
                                        producto.data.Codigo,
                                        value,
                                      );
                                      const complementLength = String(
                                        value || "",
                                      ).trim().length;

                                      if (
                                        finalCode.length >
                                        MAX_STICKER_REFERENCE_LENGTH
                                      ) {
                                        return Promise.reject(
                                          new Error(
                                            `El código final no puede superar ${MAX_STICKER_REFERENCE_LENGTH} caracteres.`,
                                          ),
                                        );
                                      }

                                      if (
                                        complementLength >
                                        remainingComplementLength
                                      ) {
                                        return Promise.reject(
                                          new Error(
                                            `El complemento no puede superar ${remainingComplementLength} caracteres con la base actual.`,
                                          ),
                                        );
                                      }

                                      return Promise.resolve();
                                    },
                                  },
                                ]}
                                tooltip="El sistema sugiere el complemento usando color y talla, pero puedes editarlo si la sugerencia no te cuadra."
                                extra={
                                  !baseReferenceCode
                                    ? "Define primero el código base en el producto general."
                                    : suggestedComplement
                                      ? `Sugerencia automática: ${suggestedComplement}. Puedes ajustarla si lo necesitas. Base + complemento no deben superar ${MAX_STICKER_REFERENCE_LENGTH}; te quedan ${remainingComplementLength} para este campo.`
                                      : `Selecciona color, talla o ambos para sugerir el complemento. Base + complemento no deben superar ${MAX_STICKER_REFERENCE_LENGTH}; te quedan ${remainingComplementLength} caracteres.`
                                }
                                required
                              >
                                <Input
                                  placeholder="Complemento editable para color y/o talla"
                                  maxLength={remainingComplementLength || 1}
                                  showCount
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        );
                      })()}
                    </Col>
                  </Row>
                </div>
                )}
              <Form.Item style={{ textAlign: "center", marginTop: "24px" }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  shape="round"
                  size="large"
                  icon={<PlusCircleFilled />}
                  style={{
                    padding: "0 32px",
                    height: "48px",
                    fontSize: "16px",
                    fontWeight: 500,
                    borderRadius: "10px",
                    transition: "all 0.3s ease",
                  }}
                >
                  {variation ? "Crear variación" : "Crear Producto"}
                </Button>
              </Form.Item>
            </Form>
          </Card>
      </Col>
    </Row>
  );
};
